import React, { useMemo } from 'react'
import { graphql, useFragment } from 'react-relay/hooks'
import { keyBy, map } from 'lodash'
import { useDecision } from '@optimizely/react-sdk'
import { useMountEffect } from 'hooks'
import ChoiceButton from 'components/ui/input/ChoiceButton'
import { FlexRow, Text, FlexColumn, Icon } from 'elements'

const integrationLabels = {
  intuit: 'QuickBooks® Online',
  xero: 'Xero®',
}

export default function FirstClientIntegrationChoices({ company, selected, onSelect }) {
  //optimizely feature
  const [decision] = useDecision('canCreateXeroReports')
  const xeroEnabled = decision.enabled

  const data = useFragment(
    graphql`
      fragment FirstClientIntegrationChoices_company on Company {
        id
        rowId
        integrations(first: 100) @connection(key: "IntegrationChoices_integrations") {
          edges {
            node {
              id
              rowId
              kind
              config
              status
            }
          }
        }
        organization {
          slug
        }
      }
    `,
    company,
  )

  const integrationOptions = useMemo(() => {
    const options = {
      intuit: {
        kind: 'intuit',
      },
      xero: {
        kind: 'xero',
      },
    }

    if (!xeroEnabled) {
      delete options.xero
    }

    return options
  }, [xeroEnabled])

  const integrations = data?.integrations?.edges?.map((edge) => edge.node)

  useMountEffect(() => {
    const match = integrations?.find(
      (integration) => integration.rowId === selected?.rowId || integration.kind === selected?.kind,
    )

    const integrationOption = integrationOptions[selected?.kind]

    if (match) {
      onSelect(match)
    } else if (integrationOption) {
      onSelect(integrationOption)
    }
  })

  const integrationsByKind = keyBy(integrations, 'kind')

  return (
    <FlexColumn width="100%">
      {map(integrationOptions, (option, kind) => {
        const integration = integrationsByKind[kind]

        const isLast = xeroEnabled ? kind === 'xero' : true
        const isSelected = selected?.kind === kind

        // Integration exists, so allow the user to select
        // it and proceed with the report
        if (integration) {
          const isEnabled = integration.status === 'ENABLED'

          return (
            <ChoiceButton
              mb={!isLast ? 'large' : null}
              key={kind}
              icon="database"
              selected={isSelected}
              area="small"
              onPress={() => onSelect(integration)}
            >
              <FlexRow width="100%" justifyContent="space-between">
                <Text color="inherit">{integrationLabels[integration.kind]} Integration</Text>
                {isEnabled && <Icon name="solid-check-circle" color="primary.0" size="medium" />}
              </FlexRow>
            </ChoiceButton>
          )
        }

        return (
          <ChoiceButton
            mb={!isLast ? 'large' : null}
            key={kind}
            icon="database"
            selected={selected?.kind === kind}
            onPress={() => onSelect(option)}
            area="small"
          >
            {integrationLabels[kind]} Integration
          </ChoiceButton>
        )
      })}
    </FlexColumn>
  )
}
